import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { bookingsApi } from '@/services/endpoints';
import { queryKeys } from '@/services/queryClient';
import type { Place } from '@/types/models';

export const useMyBookings = () =>
  useQuery({ queryKey: queryKeys.bookings.list(), queryFn: () => bookingsApi.mine() });

export const useRideBookings = (rideId: string, enabled = true) =>
  useQuery({ queryKey: queryKeys.rides.bookings(rideId), queryFn: () => bookingsApi.forRide(rideId), enabled });

export function useRequestBooking(rideId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { seats: number; message?: string; pickup?: Place; drop?: Place; idempotencyKey: string }) =>
      bookingsApi.request(rideId, {
        seats: v.seats,
        ...(v.message ? { message: v.message } : {}),
        // Both or neither: a pickup without a drop is not a segment, and the server
        // would price it as the whole route.
        ...(v.pickup && v.drop ? { pickup: v.pickup, drop: v.drop } : {}),
      }, v.idempotencyKey),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.bookings.list() });
      void qc.invalidateQueries({ queryKey: queryKeys.rides.detail(rideId) });
    },
  });
}

/** The server's price for a partial route, fetched before the passenger commits so the
 * screen can never show a number the booking would not charge. */
export function useSegmentQuote(rideId: string, pickup: Place | undefined, drop: Place | undefined) {
  return useQuery({
    queryKey: ['segmentQuote', rideId, pickup?.latitude, pickup?.longitude, drop?.latitude, drop?.longitude],
    queryFn: () => bookingsApi.quote(rideId, {
      pickupLat: (pickup as Place).latitude, pickupLng: (pickup as Place).longitude,
      dropLat: (drop as Place).latitude, dropLng: (drop as Place).longitude,
    }),
    enabled: pickup !== undefined && drop !== undefined,
    staleTime: Infinity,
  });
}

export function useSetBookingStatus(rideId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { bookingId: string; status: string; reason?: string }) =>
      bookingsApi.setStatus(v.bookingId, v.status, v.reason),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.bookings.list() });
      void qc.invalidateQueries({ queryKey: queryKeys.rides.bookings(rideId) });
      void qc.invalidateQueries({ queryKey: queryKeys.rides.detail(rideId) });
    },
  });
}
